import {
  DataGridPro, GridApiPro, GridColDef, useGridApiRef,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { MutableRefObject, useMemo } from 'react';
import CardHeader from '@mui/material/CardHeader';
import Card from '@mui/material/Card';
import { MemoizedBaseAvatar, Label } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { DEFAULT_CURRENCY } from '../../../../data';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getMembers } from '../../../../store/team/selectors';
import { getPlatformData } from '../../../../store/common/selectors';
import { useTable } from '../../../../hooks/useTable';
import {
  getDailyStateSelectedLocation, getIsOrdersLoading, getSalesmanStats,
} from '../../../../store/orders/selectors';
import { MemoizedMultiLocations } from '../../../team/components/Table/MultiLocations';
import { AnimatedDivider } from '../../../../components';

const BestSalesman = () => {
  const members = useAppSelector(getMembers);
  const { currencies } = useAppSelector(getPlatformData);
  const apiRef = useGridApiRef() as MutableRefObject<GridApiPro>;
  const dailyStateSelectedLocation = useAppSelector(getDailyStateSelectedLocation);
  const { t } = useTranslation(['inputs', 'dashboard', 'timesheets', 'commissions', 'orders', 'common']);
  const { localText } = useTable(apiRef);
  const salesManStats = useAppSelector(getSalesmanStats);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const tableData = useMemo(() => {
    if (!salesManStats) return [];

    const aggregatedSales: Record<string, { userId: string; totalOrders: number; totalSales: number }> = {};

    if (dailyStateSelectedLocation === 0) {
      // Merge all locations and aggregate sales by email
      Object.values(salesManStats).forEach((locationSales) => {
        locationSales.forEach(({ userId, totalOrders, totalSales }) => {
          if (!aggregatedSales[userId]) {
            aggregatedSales[userId] = { userId, totalOrders: 0, totalSales: 0 };
          }
          aggregatedSales[userId].totalOrders += totalOrders;
          aggregatedSales[userId].totalSales += totalSales;
        });
      });
    } else {
      const selectedSales = salesManStats[dailyStateSelectedLocation] || [];
      selectedSales.forEach(({ userId, totalOrders, totalSales }) => {
        aggregatedSales[userId] = { userId, totalOrders, totalSales };
      });
    }

    const userSalesArray = Object.values(aggregatedSales).map((userSales) => {
      const member = members.find((m) => m.email === userSales.userId) || {};
      return {
        ...userSales,
        member,
      };
    });

    return userSalesArray
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 5)
      .map((user, index) => ({
        ...user,
        rank: `Top ${index + 1}`,
      }));
  }, [salesManStats, dailyStateSelectedLocation, members]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />;

  const columnVisibilityModel = {
    locations: dailyStateSelectedLocation === 0,
  };

  const productsColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('seller', { ns: 'dashboard' }),
      width: 200,
      renderCell: ({ row }) => {
        const { member } = row;
        return (
          <Box display="flex" alignItems="center" height="100%">
            <MemoizedBaseAvatar avatarFile={member?.avatarFile || null} avatarName={member?.name || ''} />
            <Typography variant="caption">
              {member?.name ? startCase(member?.name) : row.userId}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'totalOrders',
      headerName: t('totalOrders', { ns: 'timesheets' }),
      width: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="caption">
            {row.totalOrders || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'totalSales',
      headerName: t('totalSales', { ns: 'commissions' }),
      minWidth: 130,
      renderCell: ({ row }) => {
        const currency = currencies.find((cr) => cr.code === row.member?.currency) || DEFAULT_CURRENCY;

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="caption">
              {`${row.totalSales?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} ${currency.mark}`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'locations',
      headerName: t('locations'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedMultiLocations locations={row.member.locations} />
        </Box>
      ),
    },
    {
      field: 'rank',
      headerName: t('rank', { ns: 'orders' }),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Label
            variant="soft"
            color={
              (row.rank === 'Top 1' && 'primary')
              || (row.rank === 'Top 2' && 'info')
              || (row.rank === 'Top 3' && 'success')
              || (row.rank === 'Top 4' && 'warning')
              || 'error'
            }
          >
            {row.rank}
          </Label>
        </Box>
      ),
    },
  ];

  return (
    <Card sx={{ height: 492, pb: 6 }}>
      <CardHeader
        sx={{
          p: 2,
        }}
        title={t('bestSalesman', { ns: 'orders' })}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <DataGridPro
        apiRef={apiRef}
        rows={tableData || []}
        localeText={localText}
        columnVisibilityModel={columnVisibilityModel}
        sx={{
          px: 0,
          '.MuiDataGrid-main > *:first-of-type': {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        }}
        slots={{
          noRowsOverlay: renderCustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            printOptions: {
              hideFooter: true,
              hideToolbar: true,
            },
          },
        }}
        getRowId={(row) => row?.userId || row?.member?.email || row?.id}
        columns={productsColumns}
        hideFooterPagination
        disableRowSelectionOnClick
        hideFooter
        disableColumnMenu
        checkboxSelection={false}
      />
    </Card>
  );
};

export {
  BestSalesman,
};
